import { useCallback, useEffect, useState } from 'react';
import type { AxiosError } from 'axios';
import { appApi } from '../utils/api';
import type { AppDownloadDetail } from '../models/interface/app-download.interface';

type UseAppDownloadDetailResult = {
  appDownloadDetail: AppDownloadDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
};

export const useAppDownloadDetail = (id?: number | null): UseAppDownloadDetailResult => {
  const [appDownloadDetail, setAppDownloadDetail] = useState<AppDownloadDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAppDownloadDetail = useCallback(async (): Promise<void> => {
    if (!id) {
      setAppDownloadDetail(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await appApi.get<AppDownloadDetail>(`/api/app/apps/${id}`);
      setAppDownloadDetail(res.data);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const message =
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to load app download detail';

      setError(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAppDownloadDetail();
  }, [fetchAppDownloadDetail]);

  return { appDownloadDetail, loading, error, refetch: fetchAppDownloadDetail };
};
